import { ReactNode } from 'react';

import {
  Robot as RobotIcon,
  RobotOff as RobotOffIcon,
} from '@/components/chariot-ui';
import { Tooltip } from '@/components/Tooltip';
import { Risk, RiskStatus } from '@/types';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import { isMachineHistoryAuthor } from '@/utils/userDisplay.util';

interface GitHubRepo {
  name: string;
  enum_time: string;
  permissions: {
    admin: boolean;
    maintain: boolean;
    push: boolean;
    triage: boolean;
    pull: boolean;
  };
  can_fork: boolean;
  runner_workflows: string[];
  accessible_runners: string[];
  repo_runners: string[];
  repo_secrets: string[];
  org_secrets: string[];
}

interface GitHubEnumeration {
  username: string;
  scopes: string[];
  enumeration: {
    timestamp: string;
    organizations: unknown[];
    repositories: GitHubRepo[];
  };
}

function isGitHubEnumeration(
  file: unknown
): file is Partial<GitHubEnumeration> {
  return (
    typeof file === 'object' &&
    file !== null &&
    'username' in file &&
    'scopes' in file &&
    'enumeration' in file
  );
}

function processGitHubEnumeration(
  file: Partial<GitHubEnumeration>
): Record<string, unknown> {
  const repoDetails = file.enumeration?.repositories?.map(repo => ({
    'Repository Name': repo.name,
    'Enumeration Time': repo.enum_time,
    Permissions: repo.permissions,
    'Can Fork': repo.can_fork,
    'Runner Workflows': repo.runner_workflows,
    'Accessible Runners': repo.accessible_runners,
    'Repo Runners': repo.repo_runners,
    'Repo Secrets': repo.repo_secrets,
    'Org Secrets': repo.org_secrets,
  }));

  const data = {
    Repositories: JSON.stringify(repoDetails, null, 2) ?? '',
    Scopes: JSON.stringify(file.scopes, null, 2) ?? '',
    Timestamp: file.enumeration?.timestamp ?? '',
    Username: file.username ?? '',
  };

  return data;
}

interface NucleiTemplate {
  'curl-command': string;
  request: string;
  'template-encoded': string;
  response: string;
  'extracted-results'?: string[];
  'matched-at'?: string;
}

function isNucleiTemplate(file: unknown): file is Partial<NucleiTemplate> {
  return typeof file === 'object' && file !== null && 'template-id' in file;
}

function processNucleiTemplate(
  file: Partial<NucleiTemplate>
): Record<string, unknown> {
  const decodedRule = file['template-encoded']
    ? atob(file['template-encoded'])
    : '';

  return {
    Response: file.response?.trim() ?? '[Not Available]',
    Request: file.request?.trim() ?? '[Not Available]',
    Command: file['curl-command']?.trim() ?? '[Not Available]',
    Extracted: file['extracted-results'] ?? '[Not Available]',
    'Matched At': file['matched-at'] ?? '[Not Available]',
    Rule: decodedRule,
  };
}

interface NoseyParkerTemplate {
  finding_id: number;
  rule_name: string;
  rule_text_id: string;
  rule_structural_id: string;
  num_matches: number;
  matches: {
    provenance: {
      repo_path: string;
      first_commit: {
        commit_metadata: {
          commit_id: string;
          committer_name: string;
          committer_email: string;
          message: string;
        };
        blob_path: string;
      };
    }[];
    blob_id: string;
    snippet: {
      before: string;
      matching: string;
      after: string;
    };
    location: {
      source_span: {
        start: {
          line: number;
        };
        end: {
          line: number;
        };
      };
    };
  }[];
}

function isNoseyParkerTemplate(
  file: unknown
): file is Partial<NoseyParkerTemplate> {
  return typeof file === 'object' && file !== null && 'finding_id' in file;
}

function processNoseyParkerTemplate(
  file: Partial<NoseyParkerTemplate>
): Record<string, unknown> {
  if (typeof file !== 'object' || file === null) {
    return {};
  }

  const formattedMatches = (file.matches || []).map(match => ({
    'Repository Path': match.provenance?.[0]?.repo_path || '',
    'First Commit ID':
      match.provenance?.[0]?.first_commit?.commit_metadata?.commit_id || '',
    'First Committer Name':
      match.provenance?.[0]?.first_commit?.commit_metadata?.committer_name ||
      '',
    'First Committer Email':
      match.provenance?.[0]?.first_commit?.commit_metadata?.committer_email ||
      '',
    'First Commit Message':
      match.provenance?.[0]?.first_commit?.commit_metadata?.message || '',
    'File Path': match.provenance?.[0]?.first_commit?.blob_path || '',
    'Blob ID': match.blob_id || '',
    'Snippet Before': match.snippet?.before || '',
    'Snippet Matching': match.snippet?.matching || '',
    'Snippet After': match.snippet?.after || '',
    'Start Line': String(match.location?.source_span?.start?.line || ''),
    'End Line': String(match.location?.source_span?.end?.line || ''),
  }));

  return {
    Matches: JSON.stringify(formattedMatches, null, 2),
    'Number of Matches': String(file.num_matches || 0),
    'Finding ID': String(file.finding_id || ''),
    'Rule Name': String(file.rule_name || ''),
    'Rule Text ID': String(file.rule_text_id || ''),
    'Rule Structural ID': String(file.rule_structural_id || ''),
  };
}

interface NetworkTemplate {
  ip: string;
  metadata: {
    entries: {
      address: string;
      owner: string;
      program: number;
      protocol: string;
      version: number;
    }[];
  };
  port: number;
  protocol: string;
  tls: boolean;
  transport: string;
}

function isNetworkTemplate(file: unknown): file is Partial<NetworkTemplate> {
  return (
    typeof file === 'object' &&
    file !== null &&
    'ip' in file &&
    'metadata' in file
  );
}

function processNetworkTemplate(
  file: Partial<NetworkTemplate>
): Record<string, unknown> {
  const formattedEntries = (file?.metadata?.entries || []).map(entry => ({
    Address: entry.address || '',
    Owner: entry.owner || '',
    Program: String(entry.program || ''),
    Protocol: entry.protocol || '',
    Version: String(entry.version || ''),
  }));

  return {
    IP: String(file.ip || ''),
    Port: String(file.port || ''),
    Protocol: String(file.protocol || ''),
    TLS: String(file.tls || ''),
    Transport: String(file.transport || ''),
    Metadata: JSON.stringify(formattedEntries, null, 2),
  };
}

const getDescription = (file: unknown): Record<string, unknown> => {
  if (isNucleiTemplate(file)) {
    return processNucleiTemplate(file as Partial<NucleiTemplate>);
  } else if (isGitHubEnumeration(file)) {
    return processGitHubEnumeration(file as Partial<GitHubEnumeration>);
  } else if (isNoseyParkerTemplate(file)) {
    return processNoseyParkerTemplate(file as Partial<NoseyParkerTemplate>);
  } else if (isNetworkTemplate(file)) {
    return processNetworkTemplate(file as Partial<NetworkTemplate>);
  } else if (typeof file === 'object' && file !== null) {
    // Function to iterate over keys and create sections if applicable
    const createSections = (
      data: Record<string, unknown>
    ): Record<string, unknown> => {
      const sections: Record<string, unknown> = {};
      Object.entries(data).forEach(([key, value]) => {
        sections[key] =
          typeof value === 'object' && value !== null
            ? JSON.stringify(value, null, 2)
            : String(value);
      });
      return sections;
    };

    // Create sections for the parsed file
    return createSections(file as Record<string, unknown>);
  } else if (file !== undefined) {
    return { Data: file?.toString() || '' };
  }

  return {};
};

/**
 * Non-capability risk sources. Findings with these sources were filed
 * manually (or by an external reporter like HackerOne) — they have no
 * originating capability to re-run, so a rescan makes no sense.
 */
const manualRiskSources = new Set<string>(['provided', 'hackerone']);

/**
 * Returns true when a risk was filed manually (by a human operator or an
 * external reporter like HackerOne) rather than by a platform capability.
 * Manual sources look like email addresses (e.g. `noah.tutt@praetorian.com`),
 * the sentinel `'provided'`, or an external reporter name like `'hackerone'`;
 * capability sources are short lowercase names (`nuclei`, `subdomain`, ...).
 */
export const isManualRisk = (risk: Risk): boolean => {
  const source = risk?.source ?? '';
  return source.includes('@') || manualRiskSources.has(source);
};

/**
 * Returns true when a risk was created by a Praetorian operator — its `source`
 * is a `@praetorian.com` email address. This is STRICTER than `isManualRisk`
 * (which also treats external reporters like HackerOne and the `'provided'`
 * sentinel as manual). It gates the drawer's "Change Surface" edit affordance
 * so we never offer a surface write on a finding the backend would reject as
 * unauthorized (only Praetorian-authored findings are reclassifiable this way).
 *
 * MUST mirror the backend predicate `praetorian.IsEmployee`
 * (backend/pkg/lib/praetorian/praetorian.go) exactly and stay in lockstep with
 * it: case-SENSITIVE `@praetorian.com` suffix, excluding the `chariot+` and
 * `guard+` system-account prefixes. Do not lowercase the source — the backend
 * does not, so lowercasing here would diverge the gate from the authorization
 * decision.
 */
export const isPraetorianCreatedRisk = (risk: Risk): boolean => {
  const source = risk?.source ?? '';
  return (
    source.endsWith('@praetorian.com') &&
    !source.startsWith('chariot+') &&
    !source.startsWith('guard+')
  );
};

/**
 * Returns true when a rescan makes sense for the given risk — i.e. the risk
 * has an originating capability that can be re-run. Manual findings have no
 * capability to re-run, so they cannot be rescanned.
 */
export const canRescanRisk = (risk: Risk): boolean => !isManualRisk(risk);

/**
 * Returns true when at least one risk in the set can be rescanned. Used to
 * decide whether to show a bulk rescan action — if every selected risk is
 * manual, there is nothing to rescan.
 */
export const canRescanAnyRisk = (risks: Risk[]): boolean =>
  risks.some(canRescanRisk);

/**
 * Source attribution stamped on every risk filed by a hunt agent via the
 * backend `report_new_risk` tool. Mirrors `HuntAgentSource` in
 * backend/pkg/lib/agent/tools/evidence/filing.go.
 */
const HUNT_AGENT_SOURCE = 'marcus@praetorian.com';

/**
 * Origin recorded on hunt-filed risks. Mirrors `HuntAgentOrigin` /
 * `common.HasHuntOrigin` in the backend.
 */
const HUNT_ORIGIN = 'hunt';

/**
 * Returns true when a risk was discovered by a hunt agent (filed through the
 * `report_new_risk` tool) rather than by a normal capability. These findings
 * have no originating capability to re-run — the agent that re-examines them
 * is cato-agent — so they get a "Rescan with Marcus" action instead of the
 * standard "Rescan Affected Assets". Mirrors the backend's `common.HasHuntOrigin`
 * (origins contains "hunt") with the marcus source as a fallback signal.
 */
export const isHuntFinding = (risk: Risk): boolean =>
  (risk?.origins?.includes(HUNT_ORIGIN) ?? false) ||
  risk?.source === HUNT_AGENT_SOURCE;

/**
 * Returns true when there is at least one selected risk and every selected
 * risk is a hunt finding. Used to swap the rescan action over to
 * "Rescan with Marcus" — a mixed selection falls back to the standard rescan.
 */
export const allHuntFindings = (risks: Risk[]): boolean =>
  risks.length > 0 && risks.every(isHuntFinding);

export { getDescription };

export function getMachineRecommendation(risk: Risk) {
  const { status } = getRiskStatusLabel(risk.status);

  if (status !== RiskStatus.Triaged) {
    return;
  }

  const machineHistory = risk?.history?.find?.(history =>
    getMachineRiskComment(history)
  );

  return machineHistory && getMachineRiskComment(machineHistory);
}

export function getMachineRiskComment(history: Risk['history'][number]):
  | {
      comment: ReactNode;
    }
  | undefined {
  const machineHistory = isMachineHistoryAuthor(history.by);

  if (machineHistory) {
    const recommendedToOpen = history.comment?.includes(
      'ML Model Prediction: O'
    );

    if (recommendedToOpen) {
      return {
        comment: (
          <Tooltip title="Recommended to Open">
            <span>
              <RobotIcon className="mt-[-2px] inline size-5 shrink-0 text-xl text-green-400" />
            </span>
          </Tooltip>
        ),
      };
    }
    const recommendedToDelete = history.comment?.includes(
      'ML Model Prediction: D'
    );

    if (recommendedToDelete) {
      return {
        comment: (
          <Tooltip title="Recommended to Close">
            <span>
              <RobotOffIcon className="mt-[-2px] inline size-5 shrink-0 text-xl text-red-400" />
            </span>
          </Tooltip>
        ),
      };
    }
  }
}
